import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';

const COIN_POUCH_URL = '/images/coin-pouch.png';
const ROCK_CAKE_URL = '/images/rock-cake.png';
const INVENTORY_BG_URL = '/images/inventory-bg.png';

const GRID_COLS = 4;
const GRID_ROWS = 7;
const TOTAL_SLOTS = GRID_COLS * GRID_ROWS; // 28 slots

// TRUE OSRS inventory measurements
const INVENTORY_WIDTH = 293;
const INVENTORY_HEIGHT = 404;
const FIRST_SLOT_X = 49.5;
const FIRST_SLOT_Y = 55.9;
const SLOT_WIDTH = 44;
const SLOT_HEIGHT = 38;
const HORIZONTAL_GAP = 5;
const VERTICAL_GAP = 6;

const ITEMS = [
  { id: 'coin_pouch', name: 'Coin Pouch', type: 'safe', imageUrl: COIN_POUCH_URL },
  { id: 'poison_karambwan', name: 'Poison Karambwan', type: 'bad', imageUrl: ROCK_CAKE_URL }
];

const Game = () => {
  const [gameState, setGameState] = useState('menu'); // 'menu', 'countdown', 'playing', 'gameover', 'scoreboard'
  const [coins, setCoins] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [inventory, setInventory] = useState([]);
  const [flashSlot, setFlashSlot] = useState(null);
  const [difficulty, setDifficulty] = useState(1);
  const [selectedDifficulty, setSelectedDifficulty] = useState('normal'); // 'easy', 'normal', 'hard'
  const [timeLeft, setTimeLeft] = useState(12);
  const [countdownPhase, setCountdownPhase] = useState('instructions'); // 'instructions' or 'numbers'
  const [countdownNumber, setCountdownNumber] = useState(3);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [plusOneAnimation, setPlusOneAnimation] = useState(null); // { index, visible }
  
  const gameLoopRef = useRef(null);
  const spawnIntervalRef = useRef(600);
  const coinPouchChanceRef = useRef(0.08);
  const difficultyTimerRef = useRef(null);
  const timerRef = useRef(null);

  // Difficulty settings
  const difficultySettings = {
    easy: { startTime: 15, bonusTime: 15 },
    normal: { startTime: 12, bonusTime: 12 },
    hard: { startTime: 10, bonusTime: 10 }
  };

  // Audio refs
  const clickSoundRef = useRef(null);
  const gameOverSoundRef = useRef(null);
  const deathAudioRef = useRef(null);

  // Load best score and score history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('osrs_game_best_score');
    if (saved) {
      setBestScore(parseInt(saved, 10));
    }
    
    const history = localStorage.getItem('osrs_game_score_history');
    if (history) {
      setScoreHistory(JSON.parse(history));
    }
    
    // Preload death sound
    deathAudioRef.current = new Audio('/death-sound.mp3');
    deathAudioRef.current.volume = 0.5;
  }, []);

  // Initialize audio
  useEffect(() => {
    // Create simple audio using Web Audio API for click sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    clickSoundRef.current = () => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    };

    gameOverSoundRef.current = () => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.3);
      oscillator.type = 'sawtooth';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    };
  }, []);

  // Initialize inventory with rock cakes and exactly 3 coin pouches
  const initializeInventory = () => {
    const newInventory = [];
    for (let i = 0; i < TOTAL_SLOTS; i++) {
      newInventory.push(ITEMS[1]); // Fill with rock cakes
    }
    
    // Spawn exactly 3 coin pouches
    const usedSlots = new Set();
    for (let i = 0; i < 3; i++) {
      let randomSlot;
      do {
        randomSlot = Math.floor(Math.random() * TOTAL_SLOTS);
      } while (usedSlots.has(randomSlot));
      
      usedSlots.add(randomSlot);
      newInventory[randomSlot] = ITEMS[0]; // Coin pouch
    }
    
    return newInventory;
  };

  // Start game - initialize countdown
  const startGame = (mode) => {
    setSelectedDifficulty(mode);
    const settings = difficultySettings[mode];
    
    setGameState('countdown');
    setCoins(0);
    setTimeLeft(settings.startTime);
    setDifficulty(1);
    setCountdownPhase('instructions');
    setCountdownNumber(3);
    spawnIntervalRef.current = 600;
    coinPouchChanceRef.current = 0.08;
    setInventory(initializeInventory());
    
    // Show instructions for 2 seconds
    setTimeout(() => {
      setCountdownPhase('numbers');
      startCountdown();
    }, 2000);
  };

  // Countdown from 3, 2, 1
  const startCountdown = () => {
    let count = 3;
    setCountdownNumber(count);
    
    const countdownInterval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownNumber(count);
      } else {
        clearInterval(countdownInterval);
        // Start actual game
        setGameState('playing');
        startGameLoop();
        startDifficultyIncrease();
        startTimer();
      }
    }, 1000);
  };

  // Start timer
  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - game over IMMEDIATELY
          clearInterval(timerRef.current);
          timerRef.current = null;
          stopGame();
          
          // Play death sound
          if (deathAudioRef.current) {
            deathAudioRef.current.currentTime = 0;
            deathAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
          }
          
          // Update best score and save to history immediately
          setCoins(currentCoins => {
            if (currentCoins > bestScore) {
              setBestScore(currentCoins);
              localStorage.setItem('osrs_game_best_score', currentCoins.toString());
            }
            
            // Save score to history with difficulty
            const newScore = {
              score: currentCoins,
              difficulty: selectedDifficulty,
              date: new Date().toLocaleDateString('en-US')
            };
            setScoreHistory(prevHistory => {
              const updatedHistory = [newScore, ...prevHistory].slice(0, 10);
              localStorage.setItem('osrs_game_score_history', JSON.stringify(updatedHistory));
              return updatedHistory;
            });
            
            return currentCoins;
          });
          
          // Immediately show game over
          setGameState('gameover');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Game loop - randomly change slots but maintain exactly 3 coin pouches
  const startGameLoop = () => {
    const loop = () => {
      setInventory(prev => {
        const newInventory = [...prev];
        const slotsToChange = Math.floor(Math.random() * 3) + 2; // 2-4 slots
        
        // Get current coin pouch positions
        const coinPouchSlots = [];
        prev.forEach((item, idx) => {
          if (item.type === 'safe') {
            coinPouchSlots.push(idx);
          }
        });
        
        for (let i = 0; i < slotsToChange; i++) {
          const randomSlot = Math.floor(Math.random() * TOTAL_SLOTS);
          // Don't change coin pouch slots
          if (!coinPouchSlots.includes(randomSlot)) {
            newInventory[randomSlot] = ITEMS[1]; // Rock cake
          }
        }
        
        return newInventory;
      });
      
      gameLoopRef.current = setTimeout(loop, spawnIntervalRef.current);
    };
    
    gameLoopRef.current = setTimeout(loop, spawnIntervalRef.current);
  };

  // Increase difficulty over time
  const startDifficultyIncrease = () => {
    difficultyTimerRef.current = setInterval(() => {
      setDifficulty(prev => {
        const newDiff = prev + 1;
        // Speed up spawn interval
        spawnIntervalRef.current = Math.max(250, 600 - (newDiff * 30));
        // Reduce coin pouch spawn rate slightly
        coinPouchChanceRef.current = Math.max(0.04, 0.08 - (newDiff * 0.003));
        return newDiff;
      });
    }, 3000); // Increase difficulty every 3 seconds
  };

  // Stop game
  const stopGame = () => {
    if (gameLoopRef.current) {
      clearTimeout(gameLoopRef.current);
      gameLoopRef.current = null;
    }
    if (difficultyTimerRef.current) {
      clearInterval(difficultyTimerRef.current);
      difficultyTimerRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Handle slot click
  const handleSlotClick = (index, item) => {
    if (gameState !== 'playing' || timeLeft <= 0) return; // Prevent clicks if game not playing or time is up
    
    if (item.type === 'safe') {
      // Correct click - coin pouch
      if (clickSoundRef.current) clickSoundRef.current();
      
      // Show +1 animation
      setPlusOneAnimation({ index, visible: true });
      setTimeout(() => setPlusOneAnimation(null), 600);
      
      // Flash green briefly
      setFlashSlot({ index, color: 'green' });
      setTimeout(() => setFlashSlot(null), 100);
      
      // Add coin
      const newCoins = coins + 1;
      setCoins(newCoins);
      
      // Every 40 coins, add bonus time based on difficulty
      if (newCoins % 40 === 0) {
        const settings = difficultySettings[selectedDifficulty];
        setTimeLeft(prev => prev + settings.bonusTime);
      }
      
      // Move coin pouch to a new random slot (maintain 3 coin pouches)
      setInventory(prev => {
        const newInventory = [...prev];
        
        // Remove coin pouch from current slot
        newInventory[index] = ITEMS[1]; // Karambwan
        
        // Find a new slot (that doesn't have a coin pouch AND is not the current index)
        let newSlot;
        do {
          newSlot = Math.floor(Math.random() * TOTAL_SLOTS);
        } while (newInventory[newSlot].type === 'safe' || newSlot === index);
        
        // Place coin pouch in new slot
        newInventory[newSlot] = ITEMS[0];
        
        return newInventory;
      });
    } else {
      // Wrong click - game over IMMEDIATELY
      // Set game state to prevent any further clicks
      setGameState('gameover');

      // Play death sound
      if (deathAudioRef.current) {
        deathAudioRef.current.currentTime = 0;
        deathAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
      }

      // Stop game immediately
      stopGame();

      // Flash red briefly
      setFlashSlot({ index, color: 'red' });
      setTimeout(() => setFlashSlot(null), 300);

      // Update best score and save to history
      if (coins > bestScore) {
        setBestScore(coins);
        localStorage.setItem('osrs_game_best_score', coins.toString());
      }

      // Save score to history with difficulty
      const newScore = {
        score: coins,
        difficulty: selectedDifficulty,
        date: new Date().toLocaleDateString('en-US')
      };
      const updatedHistory = [newScore, ...scoreHistory].slice(0, 10);
      setScoreHistory(updatedHistory);
      localStorage.setItem('osrs_game_score_history', JSON.stringify(updatedHistory));
    }
  };

  // Restart game
  const restartGame = () => {
    startGame(selectedDifficulty);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopGame();
    };
  }, []);

  // Calculate slot position based on row and column
  const getSlotPosition = (index) => {
    const row = Math.floor(index / GRID_COLS);
    const col = index % GRID_COLS;
    
    const x = FIRST_SLOT_X + (col * (SLOT_WIDTH + HORIZONTAL_GAP));
    const y = FIRST_SLOT_Y + (row * (SLOT_HEIGHT + VERTICAL_GAP));
    
    return { x, y };
  };

  return (
    <div className="min-h-screen bg-[#1a1612] flex items-center justify-center p-4" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      <div className="relative">
        {/* Menu Screen */}
        {gameState === 'menu' && (
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              Inventory Clicker
            </h1>
            <p className="text-xl mb-8" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
              Click the coin pouch, avoid the poisoned karambwan!
            </p>
            <div className="mb-8">
              <div className="text-2xl mb-2" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                Best Score: {bestScore} Coins
              </div>
            </div>
            
            {/* Difficulty Selection */}
            <div className="mb-6">
              <div className="text-xl mb-4" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                Select Difficulty:
              </div>
              <div className="flex gap-4 justify-center">
                <Button 
                  onClick={() => startGame('easy')}
                  className="bg-[#4CAF50] hover:bg-[#45a049] border-2 border-[#3a2f23] text-xl px-8 py-6 font-bold"
                  style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}
                >
                  Easy
                </Button>
                <Button 
                  onClick={() => startGame('normal')}
                  className="bg-[#ffaa00] hover:bg-[#e69900] border-2 border-[#3a2f23] text-xl px-8 py-6 font-bold"
                  style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#000' }}
                >
                  Normal
                </Button>
                <Button 
                  onClick={() => startGame('hard')}
                  className="bg-[#ff4444] hover:bg-[#e63939] border-2 border-[#3a2f23] text-xl px-8 py-6 font-bold"
                  style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}
                >
                  Hard
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Countdown Screen */}
        {gameState === 'countdown' && (
          <div className="flex flex-col items-center">
            {/* Score Display Placeholder - Match playing screen height */}
            <div className="mb-6 flex items-center justify-center gap-12" style={{ opacity: 0 }}>
              <div className="text-center">
                <div className="text-sm mb-1" style={{ fontFamily: 'RuneScape, Georgia, serif' }}>
                  HI-SCORE
                </div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif' }}>
                  0
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm mb-1" style={{ fontFamily: 'RuneScape, Georgia, serif' }}>
                  COINS
                </div>
                <div className="text-7xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif' }}>
                  0
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm mb-1" style={{ fontFamily: 'RuneScape, Georgia, serif' }}>
                  TIME
                </div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif' }}>
                  00
                </div>
              </div>
            </div>
            
            {/* Bonus Message Placeholder */}
            <div className="mb-4 text-center" style={{ opacity: 0 }}>
              <div className="text-base" style={{ fontFamily: 'RuneScape, Georgia, serif' }}>
                40 clicks until +20s bonus
              </div>
            </div>
            
            {/* Stop Button Placeholder */}
            <div className="mb-6" style={{ opacity: 0 }}>
              <Button className="px-4 py-2">
                Stop Game
              </Button>
            </div>
            
            {/* Blurred Inventory in Background */}
            <div 
              className="relative"
              style={{
                width: `${INVENTORY_WIDTH}px`,
                height: `${INVENTORY_HEIGHT}px`,
                backgroundImage: `url(${INVENTORY_BG_URL})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'blur(4px)',
                transform: 'scale(1.4)',
                transformOrigin: 'center'
              }}
            >
              {/* Blurred items */}
              {inventory.map((item, index) => {
                const { x, y } = getSlotPosition(index);
                
                return (
                  <div
                    key={index}
                    className="absolute"
                    style={{
                      left: `${x}px`,
                      top: `${y}px`,
                      width: `${SLOT_WIDTH}px`,
                      height: `${SLOT_HEIGHT}px`
                    }}
                  >
                    <img 
                      src={item.imageUrl} 
                      alt={item.name}
                      className="w-full h-full object-contain p-1 pointer-events-none"
                      draggable="false"
                    />
                  </div>
                );
              })}
            </div>
            
            {/* Countdown Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              {countdownPhase === 'instructions' ? (
                <div className="text-center bg-black/70 p-8 rounded-lg">
                  <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                    Collect coins &amp; don&apos;t get poisoned!
                  </h2>
                  <p className="text-2xl" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#4CAF50', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                    +{difficultySettings[selectedDifficulty].bonusTime} seconds per 40 coins!
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-8xl font-bold mb-4" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00', textShadow: '4px 4px 8px rgba(0,0,0,0.8)' }}>
                    {countdownNumber}
                  </div>
                  <p className="text-3xl" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                    Open the coin pouches!
                  </p>
                </div>
              )}
            </div>
            
            {/* Progress Bar Placeholder - Match playing screen */}
            <div className="mt-20 w-96" style={{ opacity: 0 }}>
              <div className="h-6 rounded overflow-hidden">
                <div className="h-full w-full" />
              </div>
            </div>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && (
          <div className="flex flex-col items-center">
            {/* Score Display - Horizontal Layout */}
            <div className="mb-6 flex items-center justify-center gap-12">
              {/* High Score - Left */}
              <div className="text-center">
                <div className="text-sm mb-1" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#999' }}>
                  HI-SCORE
                </div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#fff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                  {bestScore}
                </div>
              </div>
              
              {/* Current Score - Center (Biggest) */}
              <div className="text-center">
                <div className="text-sm mb-1" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                  COINS
                </div>
                <div className="text-7xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                  {coins}
                </div>
              </div>
              
              {/* Time - Right */}
              <div className="text-center">
                <div className="text-sm mb-1" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#999' }}>
                  TIME
                </div>
                <div className="text-3xl font-bold" style={{ 
                  fontFamily: 'RuneScape, Georgia, serif',
                  color: timeLeft <= 5 ? '#ff4444' : timeLeft <= 10 ? '#ffaa00' : '#4CAF50',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                }}>
                  {timeLeft.toString().padStart(2, '0')}
                </div>
              </div>
            </div>
            
            {/* Bonus Message */}
            <div className="mb-4 text-center">
              <div className="text-base" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                {40 - (coins % 40)} clicks until +{difficultySettings[selectedDifficulty].bonusTime}s bonus
              </div>
            </div>
            
            {/* Stop Button */}
            <Button 
              onClick={() => {
                stopGame();
                setGameState('menu');
              }}
              className="mb-6 bg-[#534431] hover:bg-[#6b5942] border-2 border-[#3a2f23] px-4 py-2"
              style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}
            >
              Stop Game
            </Button>

            {/* Inventory Grid - Scaled up */}
            <div 
              className="relative"
              style={{
                width: `${INVENTORY_WIDTH}px`,
                height: `${INVENTORY_HEIGHT}px`,
                backgroundImage: `url(${INVENTORY_BG_URL})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                transform: 'scale(1.4)',
                transformOrigin: 'center'
              }}
            >
              {/* Invisible grid overlay with absolute positioned items */}
              {inventory.map((item, index) => {
                const { x, y } = getSlotPosition(index);
                
                return (
                  <div
                    key={index}
                    onClick={() => handleSlotClick(index, item)}
                    className="absolute cursor-pointer transition-all duration-100 active:scale-95"
                    style={{
                      left: `${x}px`,
                      top: `${y}px`,
                      width: `${SLOT_WIDTH}px`,
                      height: `${SLOT_HEIGHT}px`,
                      backgroundColor: flashSlot?.index === index 
                        ? (flashSlot.color === 'green' ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.5)')
                        : 'transparent'
                    }}
                  >
                    <img 
                      src={item.imageUrl} 
                      alt={item.name}
                      className="w-full h-full object-contain p-1 pointer-events-none"
                      draggable="false"
                    />
                    
                    {/* +1 Animation */}
                    {plusOneAnimation?.index === index && plusOneAnimation?.visible && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        style={{
                          animation: 'float-up 0.6s ease-out'
                        }}
                      >
                        <span 
                          className="text-xl font-bold"
                          style={{ 
                            fontFamily: 'RuneScape, Georgia, serif', 
                            color: '#00ff00',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.9)',
                            animation: 'fade-out 0.6s ease-out'
                          }}
                        >
                          +1
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Time Progress Bar - Outside scaled container */}
            <div className="mt-20 w-96">
              <div 
                className="h-6 rounded overflow-hidden"
                style={{
                  backgroundColor: 'rgba(50, 40, 30, 0.9)',
                  border: '2px solid #8B7355',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                <div 
                  className="h-full transition-all duration-1000 ease-linear"
                  style={{
                    width: `${(timeLeft / difficultySettings[selectedDifficulty].startTime) * 100}%`,
                    backgroundColor: timeLeft <= 5 ? '#ff4444' : timeLeft <= 10 ? '#ffaa00' : '#4CAF50',
                    boxShadow: '0 0 10px rgba(255,255,255,0.3)'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Game Over Screen */}
        {gameState === 'gameover' && (
          <div className="text-center">
            <h2 className="text-5xl font-bold mb-4" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ff4444', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              Oh dear, you are dead!
            </h2>
            <div className="mb-6">
              <div className="text-3xl mb-2" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                You earned: <span style={{ color: '#00BFFF' }}>{coins}</span> Coins
              </div>
              <div className="text-2xl mb-2" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                Best Score: <span style={{ color: '#00BFFF' }}>{bestScore}</span> Coins
              </div>
              <div className="text-xl" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#c9b583' }}>
                Difficulty: <span style={{ 
                  color: selectedDifficulty === 'easy' ? '#4CAF50' : 
                        selectedDifficulty === 'normal' ? '#ffaa00' : '#ff4444',
                  fontWeight: 'bold'
                }}>{selectedDifficulty.toUpperCase()}</span>
              </div>
            </div>
            <div className="flex gap-4 justify-center">
              <Button 
                onClick={restartGame}
                className="bg-[#534431] hover:bg-[#6b5942] border-2 border-[#3a2f23] text-xl px-8 py-6 font-bold"
                style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}
              >
                Play Again
              </Button>
              <Button 
                onClick={() => setGameState('menu')}
                className="bg-[#534431] hover:bg-[#6b5942] border-2 border-[#3a2f23] text-xl px-8 py-6 font-bold"
                style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}
              >
                Main Menu
              </Button>
              <Button 
                onClick={() => setGameState('scoreboard')}
                className="bg-[#534431] hover:bg-[#6b5942] border-2 border-[#3a2f23] text-xl px-8 py-6 font-bold"
                style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}
              >
                Scoreboard
              </Button>
            </div>
          </div>
        )}

        {/* Scoreboard Screen */}
        {gameState === 'scoreboard' && (
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              Score History
            </h2>
            
            {/* Scoreboard Table */}
            <div className="bg-[#3a2f23] border-4 border-[#534431] rounded-lg p-6 min-w-[500px]">
              <div className="grid grid-cols-3 gap-4 mb-4 pb-2 border-b-2 border-[#8B7355]">
                <div className="text-2xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                  DATE
                </div>
                <div className="text-2xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                  SCORE
                </div>
                <div className="text-2xl font-bold" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}>
                  DIFFICULTY
                </div>
              </div>
              
              {scoreHistory.length > 0 ? (
                [...scoreHistory].sort((a, b) => b.score - a.score).map((entry, index) => (
                  <div key={index} className="grid grid-cols-3 gap-4 py-2 border-b border-[#534431]">
                    <div className="text-xl" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#00BFFF' }}>
                      {entry.date}
                    </div>
                    <div className="text-xl" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#00BFFF' }}>
                      {entry.score}
                    </div>
                    <div className="text-xl" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#00BFFF' }}>
                      {entry.difficulty || 'normal'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-4 text-xl" style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#c9b583' }}>
                  No scores yet. Play a game!
                </div>
              )}
            </div>
            
            <Button 
              onClick={() => setGameState('menu')}
              className="mt-6 bg-[#534431] hover:bg-[#6b5942] border-2 border-[#3a2f23] text-xl px-8 py-4 font-bold"
              style={{ fontFamily: 'RuneScape, Georgia, serif', color: '#ffff00' }}
            >
              Back to Menu
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Game;